/**
 * Hash Parser para SidePanel Multi-Nível
 *
 * Gerencia parsing e construção de hash URLs no formato:
 * #p1=panel-name&p1_prop1=value1&p2=another-panel&p2_prop2=value2
 */

/**
 * Parse hash string para array de níveis
 * @param {string} hash - "#p1=debts&p1_clientId=123&p2=details&p2_id=456"
 * @returns {Array} - [{ level: 1, name: 'debts', props: { clientId: '123' } }, ...]
 */
export function parseHash(hash) {
  if (!hash || hash === '#') return []

  const hashString = hash.startsWith('#') ? hash.slice(1) : hash
  const params = new URLSearchParams(hashString)
  const levels = {}

  // Agrupar por nível
  for (const [key, value] of params.entries()) {
    if (key.startsWith('p')) {
      const match = key.match(/^p(\d+)(?:_(.+))?$/)
      if (!match) continue

      const level = parseInt(match[1], 10)
      const propName = match[2]

      if (!levels[level]) {
        levels[level] = { level, name: null, props: {} }
      }

      if (!propName) {
        // p1=debts -> nome do painel
        levels[level].name = value
      } else {
        // p1_clientId=123 -> prop
        // URLSearchParams já decodifica os valores — usar value diretamente
        try {
          levels[level].props[propName] = JSON.parse(value)
        } catch {
          levels[level].props[propName] = value
        }
      }
    }
  }

  // Converter para array ordenado
  return Object.values(levels)
    .sort((a, b) => a.level - b.level)
    .filter(l => l.name) // apenas níveis com nome
}

/**
 * Build hash string a partir do stack
 * @param {Array} stack - Array de painéis
 * @returns {string} - "#p1=debts&p1_clientId=123..."
 */
export function buildHash(stack) {
  if (!stack || stack.length === 0) return ''

  const params = new URLSearchParams()

  for (const panel of stack) {
    const level = panel.level

    // Nome do painel
    params.set(`p${level}`, panel.name)

    // Props
    for (const [key, value] of Object.entries(panel.props || {})) {
      if (value !== null && value !== undefined) {
        // Skip functions - they can't be serialized to URL
        if (typeof value === 'function') continue

        // Serializar sempre via JSON — garante round-trip tipado (string "123" não vira number)
        params.set(`p${level}_${key}`, JSON.stringify(value))
      }
    }
  }

  return `#${params.toString()}`
}

/**
 * Adiciona nível ao hash atual
 * @param {string} currentHash - Hash atual
 * @param {string} panelName - Nome do painel
 * @param {object} props - Props
 * @returns {string} - Novo hash
 */
export function appendLevel(currentHash, panelName, props = {}) {
  const levels = parseHash(currentHash)
  const newLevel = levels.length + 1

  levels.push({
    level: newLevel,
    name: panelName,
    props,
  })

  return buildHash(levels.map((l, idx) => ({ ...l, level: idx + 1 })))
}

/**
 * Remove último nível do hash
 * @param {string} currentHash - Hash atual
 * @returns {string} - Novo hash
 */
export function removeLastLevel(currentHash) {
  const levels = parseHash(currentHash)
  if (levels.length === 0) return ''

  levels.pop()
  return buildHash(levels)
}

/**
 * Substitui nível atual (mantém mesma posição)
 * @param {string} currentHash - Hash atual
 * @param {number} level - Nível a substituir
 * @param {string} panelName - Novo nome
 * @param {object} props - Novas props
 * @returns {string} - Novo hash
 */
export function replaceLevel(currentHash, level, panelName, props = {}) {
  const levels = parseHash(currentHash)
  const index = levels.findIndex(l => l.level === level)

  if (index === -1) return currentHash

  levels[index] = { level, name: panelName, props }
  return buildHash(levels)
}

/**
 * Remove níveis a partir de um índice
 * @param {string} currentHash - Hash atual
 * @param {number} fromLevel - A partir de qual nível remover (inclusive)
 * @returns {string} - Novo hash
 */
export function removeLevelsFrom(currentHash, fromLevel) {
  const levels = parseHash(currentHash)
  const filtered = levels.filter(l => l.level < fromLevel)
  return buildHash(filtered)
}
